<template>
    <section>
    <Head>
        <title>{{ builderData.pageName }}</title>
        <meta name="description" :content="builderData.metaDescription" />
    </Head>
    <div id="blog-header" data-allow-mismatch class="banner banner--blog-landing banner--desktop-top-center">
        <div v-if="builderData?.blogLandingContent?.banner" class="banner__background">
            <Media
                class="banner__image banner__image--desktop" 
                role="presentation"
                :media="builderData?.blogLandingContent?.banner"
                defaultMediaType="image"
            />
            <Media
                role="presentation"
                class="banner__image banner__image--mobile" 
                :media="builderData?.blogLandingContent?.banner"
                defaultMediaType="image"
            />
        </div>
        <div v-else class="banner__background">
            <Media
                class="banner__image banner__image--desktop" 
                role="presentation"
                :media="desktopHeroImage"
                defaultMediaType="image"
            />
            <Media
                role="presentation"
                class="banner__image banner__image--mobile" 
                :media="mobileHeroImage"
                defaultMediaType="image"
            />
        </div>
        <div class="banner__content gradient">
            <div class="banner__layout center-align bg-rothcoCharcoal">
                <h2 class="banner__headline headline-xl text-rothcoWhite" role="heading" aria-level="1">{{ builderData?.blogLandingContent?.header ?? defaultHeaderText }}</h2>
                <p class="banner__description paragraph-default text-rothcoWhite" role="heading" aria-level="4">
                    {{ builderData?.blogLandingContent?.description ?? defaultDescription }}
                </p>
                <form class="input-folder--input input-folder input-search" @reset="searchTerm = ''">
                    <input type="text" id="articles-search" placeholder=" " v-model="searchTerm" @keypress.enter.prevent>
                    <label class="mobile-hidden" for="articles-search"><p class="paragraph-default">Search for an article...</p></label>
                    <label class="desktop-hidden" for="articles-search"><p class="paragraph-default">Search for an article...</p></label>
                    <button type="reset" aria-label="Clear search"></button>
                </form>
            </div>
            <div v-if="builderData?.blogLandingContent?.ctas?.length > 0" class="banner__buttons">
                <RothcoCta
                    v-for="(cta, index) in builderData.blogLandingContent.ctas"
                    :key="index"
                    :ctaType="cta.type"
                    :class="[...getCtaClasses(cta)]"
                    v-bind="getCtaLink({url: cta})"
                    :aria-label="cta.aria_label"
                    :button_icon="null"
                >
                    {{ cta.label }}
                </RothcoCta>
            </div>
        </div>
    </div>
    <div :class="{'plp': true, 'drawer-open' : showFilters}">
        <div class="filter-bar">
            <div style="z-index: 1" class="filter-breadcrumbs">
                <div class="breadcrumb-container">
                    <!-- MOBILE -->
                    <div class="breadcrumb desktop-hidden" v-if="previousBreadcrumb">
                        <ArrowGrey aria-hidden="true" />
                        <Link as="span" :href="route(previousBreadcrumb.alias)+previousBreadcrumb.filter" class="paragraph-caption text-rothcoText">
                            {{ previousBreadcrumb.label }}
                        </Link>
                    </div>
                    <!-- DESKTOP -->
                    <div v-for="(breadcrumb,index) in breadcrumbs" :key="index" class="breadcrumb mobile-hidden">
                        <ArrowGrey v-if="index > 0" class="arrow-right" aria-hidden="true" />
                        <Link as="span" :href="route(breadcrumb.alias)+breadcrumb.filter" class="paragraph-caption text-rothcoText link">
                            {{ breadcrumb.label }}
                        </Link>
                    </div>
                </div>
                <span class="filter-bar-count">{{ numberOfResults }} Results</span>
            </div>
            <div class="filter-sort-buttons">
                <button class="filter-button" aria-label="Toggle filters" @click="toggleFilters()">
                    <span class="desktop-hidden">Sort & Filters</span>
                    <span class="mobile-hidden" id="toggleFiltersButton">{{ showFilters ? "Hide" : "Show" }} Filters </span>
                    <span class="applied-filters">({{ activeFilters ? activeFilters.length : 0 }})</span>
                </button>

                <details v-if="sortOptions.length > 0" class="sort-container mobile-hidden" ref="sortDropdown">
                    <summary class="sort-button" aria-label="Sort products">
                        Sort By: {{ sortOption?.label }}
                    </summary>
                    <ul class="sort-content">
                        <li v-for="(option, index) in sortOptions" :class="{'sort-select paragraph-default': true, 'selected': option.label === sortOption?.label}" :key="index">
                            <button @click.prevent="setSortOption(option)" type="button" :aria-label="`Sort by ${option.label}`" :aria-pressed="option.label === sortOption?.label">
                                {{ option.label }}
                            </button>
                        </li>
                    </ul>
                </details>
            </div>
        </div>
        <div class="container-filter-product-grid">
            <ProductFilterDrawer :sort-options="sortOptions" v-model:active-filters="activeFilters" :facets="facets" :numResults="numberOfResults" v-model:show-drawer="showFilters"/>
            <!-- PRODUCT GRID START-->
            <div class="product-grid-container">
                <div class="product-grid blog-grid">
                    <ArticleCard v-for="(blogArticle) of data" :subject="blogArticle.category" :headline="blogArticle.name" :image="blogArticle.image" cta="View Article" :link="getBlogRoute(blogArticle)" :key="slugify(blogArticle.name)"/>
                </div>
                <pagination
                    :pagination="searchSpringPagination"
                    @pageChange="handlePagination"
                />
            </div>
        </div>
    </div>
    <Cms
        v-show="false"
        key="blog-landing"
        v-model="builderData" 
        :readable-fields="['pageName', 'metaDescription', 'blogLandingContent']"
        model="content-section"
        driver="builder"
        :components="getBuilderComponents()"
        is-section
        :search-params="searchParams"
    />
    </section>
</template>
<script lang="ts" setup>
    import { computed, ref, inject } from "vue";
    import { usePage, Head, Link } from "@inertiajs/vue3";
    // import { registeredComponents } from "../../blog-landing-init-builder";
    import { getBuilderComponents } from "@root/builder-components.ts";
    import ArticleCard from "../components/ArticleCard.vue";
    import { useDebounceFn, watchDeep } from "@vueuse/core";
    import { useContent } from "@/helpers/composables/searchSpringApi";
    import ProductFilterDrawer from "@/components/ProductFilterDrawer.vue";
    import Pagination from "@/components/Pagination.vue";
    import RothcoCta from "@/components/RothcoCta.vue"
    import Media from "@/components/Media.vue";
    import Cms from "@/components/Cms.vue";
    import desktopHeroImage from "@images/batch4/desktop-hero.jpg";
    import mobileHeroImage from "@images/batch4/mobile-hero.jpg";
    import { useCasts } from "@/helpers/composables/casts";
    import { getCtaLink, type Cta } from "@/helpers/interfaces/Cta";
    import ArrowGrey from "@icons/arrow-grey.svg";
    import { slugify } from "@/helpers/composables/slugify";
    // import { route } from "ziggy-js";
    let route = inject('route');

    type BlogLandingContent = {
        header: string
        description: string
        banner: string | null
        ctas: Cta[]
    }
    type BuilderData = {
        pageName: string
        metaDescription: string
        blogLandingContent: BlogLandingContent
    }

    const defaultHeaderText = "News & Insights";
    const defaultDescription = "Find out what's happening at Rothco, explore product launches and highlights, and more!";

    let builderData = ref<BuilderData>({
        pageName: "Rothco Military, Tactical & Outdoor Insights",
        metaDescription: "Explore Rothco’s blog for expert tips, gear guides, and industry stories on military, tactical, survival, and outdoor living.",
        blogLandingContent: {
            header: defaultHeaderText,
            description: defaultDescription,
            banner: null,
            ctas: []
        }
    })

    const searchTerm = ref('');

    let { data, searchSpringResults, handleFilter, handlePagination, handleSort } = useContent({
        filters: [],
        sorts: [],
        searchTerm: searchTerm,
        bgFilter: {
            filter: 'bgfilter.ss_category_hierarchy',
            value: 'Blog'
        }
    });
    let activeFilters = ref([]);
    let facets = computed(()=>{
        return searchSpringResults.value?.facets.filter(facet => facet.field == 'ss_category_hierarchy').map(facet => ({
            ...facet,
            label: 'Type'
        })) || [];
    })
    let numberOfResults = computed(()=>{
        return toNumber(searchSpringResults.value?.pagination?.totalResults);
    })

    const sortOptions = computed(()=>{
        return searchSpringResults.value.sorting.options ?? []
    })
    
    let getBlogRoute = ({url}) => {
        let lastPart = url.split("/").pop()
        return route('blog.show', { slug: lastPart });
        // return route('blog.show', { slug: route('blog.show', ) });
    }

    let showFilters = ref(false);
    let toggleFilters = () => {
        showFilters.value = !showFilters.value;
    }
    let handleFilterDebounce = useDebounceFn((newVal) => {
        handleFilter(newVal);
    }, 500);
    watchDeep(() => activeFilters.value, handleFilterDebounce);

    let sortOption = ref({})

    let setSortOption = (option) => {
        sortOption.value = option;
        handleSort(option);
    }

    let searchSpringPagination = computed(() => {
        // convert pagination data to match the pagination component
        return {
            totalItems: searchSpringResults.value.pagination.totalResults,
            currentPage: searchSpringResults.value.pagination.currentPage,
            pageSize: searchSpringResults.value.pagination.perPage,
            maxPages: searchSpringResults.value.pagination.totalPages
        };
    });

    let searchParams = computed(()=>({
        'data.pageUrl.$eq': 'blog',
        'data.pageType.$eq': 'Other',
    }))

    let { toNumber } = useCasts();

    const breadcrumbs = computed(()=>{
        const url = new URL(usePage().url, window.location.origin)
        const blogFilter =  url.searchParams.get("ss[ss_category_hierarchy]")
        const blogFilterBreadcrumbs = (blogFilter?.split(">") ?? ["Blog"]).map((breadcrumb,index)=>{
            return {
                label: breadcrumb,
                alias: "blog.landing",
                filter: blogFilter?.split(">").length > 0 ? `?ss[ss_category_hierarchy]=${blogFilter?.split(">").slice(0,index+1).join(">")}` : ""
            }
        });
        return [
            {
                label: "Home",
                alias: "home",
                filter: ""
            },
            ...blogFilterBreadcrumbs
        ]
    })
    const previousBreadcrumb = computed(()=>{
        return breadcrumbs.value.length > 1 ? breadcrumbs.value[breadcrumbs.value.length-2] : null
    })

    const getCtaClasses = (cta) => {
        let classes = [cta.style ? cta.style : "primary"];
        if (cta.size != "default") classes.push(cta.size);
        return classes;
    };

</script>